import React, { useState, useRef } from "react";
import { ProgressSlider } from "@/components/common/progress-slider";
import { Video } from "@/components/common/video";
import { Spinner } from "@/components/ui/modal";
import { PlayIcon } from "@/components/ui/icon";
import { cn } from "@/utils/cn";
import { processImageUrl } from "@/utils/image-url-resize";
interface VideoPlayerProps {
  src: string;
  poster?: string;
  muted?: boolean;
  loop?: boolean;
  showControlsBar?: boolean;
  className?: string;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  poster,
  muted,
  loop = false,
  showControlsBar = true,
  className = "",
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [showPlayOverlay, setShowPlayOverlay] = useState(false); // 显示点击播放蒙层
  const hasLeftMouse = useRef(true);

  // 播放视频
  const playVideo = async () => {
    if (!videoRef.current) {
      return;
    }
    if (isPlaying) {
      return;
    }
    try {
      setShowPlayOverlay(false);
      await videoRef.current.play();
    } catch (error) {
      console.warn("Video play failed:", error);
      // 检查是否是自动播放被阻止的错误
      if (error instanceof DOMException && error.name === "NotAllowedError") {
        setShowPlayOverlay(true);
      }
    }
  };

  // 暂停视频
  const pauseVideo = () => {
    if (!videoRef.current) {
      return;
    }
    videoRef.current.pause();
  };

  // 处理点击播放蒙层
  const handlePlayOverlayClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPlayOverlay(false);

    if (videoRef.current) {
      try {
        await videoRef.current.play();
      } catch (error) {
        console.warn("Manual play failed:", error);
      }
    }
  };

  // 鼠标进入事件
  const handleMouseEnter = () => {
    hasLeftMouse.current = false;
    // 设置进入延时
    setTimeout(() => {
      if (!hasLeftMouse.current && !isPlaying) {
        // 视频如果还需要加载则展示loading状态
        if (videoRef.current && videoRef.current.readyState <= 3) {
          setIsLoading(true);
        }
        playVideo();
      }
    }, 100);
  };

  // 鼠标离开事件
  const handleMouseLeave = () => {
    hasLeftMouse.current = true;
    setIsLoading(false);
    setShowPlayOverlay(false);
    pauseVideo();
  };

  // 进度条变化事件
  const handleProgressChange = (value: number) => {
    if (videoRef.current && duration > 0) {
      const newTime = (value / 100) * duration;
      videoRef.current.currentTime = newTime;
      setProgress(value);
    }
  };

  // 拖拽开始
  const handleDragStart = () => {
    setIsDragging(true);
  };

  // 拖拽结束
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // 视频时间更新
  const handleTimeUpdate = () => {
    if (!videoRef.current) {
      return;
    }
    if (isDragging) {
      return;
    }
    const current = videoRef.current.currentTime;
    const total = videoRef.current.duration || 0;

    setDuration(total);

    if (total > 0) {
      const progressPercent = (current / total) * 100;
      setProgress(progressPercent);
    }
  };

  // 视频可以播放事件
  const handleCanPlay = () => {
    setIsLoading(false);
  };

  // 视频等待数据事件
  const handleWaiting = () => {
    setIsLoading(true);
  };

  // 视频加载metadata完成
  const handleLoadedData = () => {
    if (!videoRef.current) {
      return;
    }
    setDuration(videoRef.current.duration);
  };

  // 视频正在开始播放事件
  const handlePlaying = () => {
    if (hasLeftMouse.current) {
      setIsPlaying(false);
      setIsLoading(false);
      setShowPlayOverlay(false);
      return;
    }
    setIsPlaying(true);
    setIsLoading(false);
    setShowPlayOverlay(false);
  };

  // 视频暂停事件
  const handlePause = () => {
    setIsPlaying(false);
    setIsLoading(false);
  };

  // 视频播放错误事件
  const handleError = () => {
    setIsLoading(false);
    setIsPlaying(false);
  };

  return (
    <div
      ref={containerRef}
      className={cn("relative group overflow-hidden", className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Video
        ref={videoRef}
        src={src}
        poster={processImageUrl(poster, 240)}
        muted={muted}
        loop={loop}
        className="w-full h-full object-cover rounded-sm"
        onTimeUpdate={handleTimeUpdate}
        onLoadedData={handleLoadedData}
        onCanPlay={handleCanPlay}
        onWaiting={handleWaiting}
        onPause={handlePause}
        onPlaying={handlePlaying}
        onError={handleError}
        preload="none"
        crossOrigin="anonymous"
      />

      {/* 加载蒙层 */}
      {isLoading && (
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center rounded-sm pointer-events-none">
          <Spinner className="text-white" />
        </div>
      )}

      {/* 点击播放蒙层 - 当自动播放被阻止时显示 */}
      {showPlayOverlay && (
        <div
          className="absolute inset-0 bg-black/30 flex items-center justify-center rounded-sm cursor-pointer transition-opacity duration-300"
          onClick={handlePlayOverlayClick}
        >
          <div className="flex flex-col items-center text-white">
            <PlayIcon className="w-12 h-12 text-i-normal hover:text-[rgba(40,47,62,1)]" />
          </div>
        </div>
      )}

      {/* 控制栏 */}
      {showControlsBar && (
        <div
          className="hidden group-hover:block absolute bottom-0 left-0 right-0 bg-gradient-to-t shadow-soft p-2"
          onClick={(e) => e.stopPropagation()}
        >
          <ProgressSlider
            value={progress}
            max={100}
            onChange={handleProgressChange}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            className="w-full"
          />
        </div>
      )}
    </div>
  );
};
